import { useEffect, useState, useCallback } from "react";
import { Toaster } from "sonner";
import { api, getKey } from "@/lib/api";
import AccessGate from "@/components/AccessGate";
import Sidebar from "@/components/Sidebar";
import DashboardView from "@/components/DashboardView";
import GroupsView from "@/components/GroupsView";
import LicensesView from "@/components/LicensesView";
import MembershipCenter from "@/components/MembershipCenter";
import ActivityView from "@/components/ActivityView";
import CommandsView from "@/components/CommandsView";
import EventsView from "@/components/EventsView";
import { Menu } from "lucide-react";
import { useBranding } from "@/hooks/useBranding";

export default function AdminPanel() {
  const brandName = useBranding();
  const [authed, setAuthed] = useState(!!getKey());
  const [view, setView] = useState("dashboard");
  const [stats, setStats] = useState({});
  const [status, setStatus] = useState({});
  const [groups, setGroups] = useState([]);
  const [logs, setLogs] = useState([]);
  const [mobileNav, setMobileNav] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [s, g, l] = await Promise.all([api.stats(), api.groups(), api.activity(80)]);
      setStats(s); setGroups(g); setLogs(l);
    } catch (e) { /* ignore */ }
  }, []);

  const loadStatus = useCallback(async () => {
    try { setStatus(await api.status()); } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!authed) return;
    loadData();
    loadStatus();
    const t1 = setInterval(loadStatus, 4000);
    const t2 = setInterval(loadData, 15000);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, [authed, loadData, loadStatus]);

  const logout = () => { localStorage.removeItem("acw_key"); setAuthed(false); };

  useEffect(() => {
    if (getKey()) {
      api.verifyKey(getKey()).catch((err) => {
        if (err?.response?.status === 401) logout();
      });
    }
    const onUnauth = () => setAuthed(false);
    window.addEventListener("acw-unauthorized", onUnauth);
    return () => window.removeEventListener("acw-unauthorized", onUnauth);
  }, []);

  if (!authed) return (<><AccessGate onEnter={() => setAuthed(true)} /><Toaster theme="dark" position="top-center" /></>);

  const views = {
    dashboard: <DashboardView stats={stats} status={status} />,
    grupos: <GroupsView groups={groups} reload={loadData} />,
    licencias: <LicensesView groups={groups} reload={loadData} />,
    membresias: <MembershipCenter groups={groups} reload={loadData} />,
    eventos: <EventsView />,
    actividad: <ActivityView logs={logs} />,
    comandos: <CommandsView />,
  };

  const changeView = (v) => { setView(v); setMobileNav(false); };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <Sidebar active={view} onChange={changeView} onLogout={logout} botState={status.state} />

      <div className="flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-40 h-16 flex items-center justify-between px-4 bg-obsidian/90 backdrop-blur-xl border-b border-white/10">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="logo" className="w-8 h-8 rounded object-contain" />
            <span className="font-heading font-bold" data-testid="mobile-header-brand-name">{brandName}</span>
          </div>
          <button data-testid="mobile-menu-button" onClick={() => setMobileNav((v) => !v)} className="p-2 rounded-md hover:bg-white/5">
            <Menu size={22} />
          </button>
        </header>

        {mobileNav && (
          <div className="lg:hidden border-b border-white/10 bg-obsidian p-2 grid grid-cols-2 gap-2" data-testid="mobile-nav">
            {["dashboard", "grupos", "licencias", "membresias", "eventos", "actividad", "comandos"].map((v) => (
              <button key={v} data-testid={`mobile-nav-${v}`} onClick={() => changeView(v)} className={`py-2 rounded-md text-sm capitalize ${view === v ? "bg-gold text-black font-bold" : "bg-white/5"}`}>{v}</button>
            ))}
            <button onClick={logout} className="py-2 rounded-md text-sm bg-crimson/15 text-crimson">Salir</button>
          </div>
        )}

        <main className="p-5 md:p-8 max-w-7xl mx-auto">{views[view]}</main>
      </div>

      <Toaster theme="dark" position="top-center" richColors />
    </div>
  );
}
